import { notifyError } from "common/utils/app-monitoring/notify";
import {
  trackCompositeRenderFailed,
  type CompositeRenderFailedReason
} from "./analytics/composite-render";

type DrawSource = HTMLImageElement | HTMLCanvasElement | ImageBitmap;

type DrawArgs =
  | [number, number]
  | [number, number, number, number]
  | [number, number, number, number, number, number, number, number];

export type SafeDrawImageOptions = {
  ctx: CanvasRenderingContext2D;
  source: DrawSource;
  site: string;
  args?: DrawArgs;
};

export const getDrawSourceDimensions = (source: DrawSource): { width: number; height: number } => {
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
};

export const safeDrawImage = (options: SafeDrawImageOptions): boolean => {
  const { ctx, source, site, args = [0, 0] as DrawArgs } = options;
  const { width, height } = getDrawSourceDimensions(source);

  if (width <= 0 || height <= 0) {
    trackCompositeRenderFailed({
      reason: "zero_dimensions",
      site,
      sourceWidth: width,
      sourceHeight: height
    });
    return false;
  }

  try {
    if (args.length === 2) {
      ctx.drawImage(source, args[0], args[1]);
    } else if (args.length === 4) {
      ctx.drawImage(source, args[0], args[1], args[2], args[3]);
    } else {
      ctx.drawImage(source, args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7]);
    }
    return true;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isSecurityError = error instanceof Error && error.name === "SecurityError";
    const isTaintedMessage = errorMessage.toLowerCase().includes("tainted");
    const reason: CompositeRenderFailedReason =
      isSecurityError || isTaintedMessage ? "cors_taint" : "draw_threw";
    trackCompositeRenderFailed({
      reason,
      site,
      sourceWidth: width,
      sourceHeight: height
    });
    notifyError("safeDrawImage drawImage threw", {
      params: {
        site,
        reason,
        source_width: width,
        source_height: height,
        error_message: errorMessage,
        stack_trace: error instanceof Error ? (error.stack ?? null) : null
      }
    });
    return false;
  }
};
